import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { Popup, Button } from "devextreme-react";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import { getCardConnectPayLayout } from "./CardConnectPayLayout";

const CARDCONNECT_LAYOUT_ID = "C24222C7-F580-4A28-B585-FE6F81C1047B";
const CARDCONNECT_LAYOUT_NAME = "CardConnectPayment";

const initialPopupFormFields = {
    CARDCONNECT_PAYMENT_METHOD: "card",
    CARDCONNECT_TRANS_NO: "",
    CARDCONNECT_CUSTOMER: "",
    CARDCONNECT_AMOUNT: null,
    CARDCONNECT_PROFILE: "",
    CARDCONNECT_ACCOUNT: "",
};

/** Build popup payload from Form.js `showPopup` (amount from dbo.FNOM_CALCULATETRANSTOTALS(ROWID,'OutARBalanceDue')). */
function cardConnectPayloadFromShowPopup(showPopup) {
    if (!showPopup) return {};
    return {
        transactionNumber: showPopup.transactionNumber ?? "",
        customer: showPopup.customer ?? "",
        amount: showPopup.amount,
        paymentProfile: showPopup.paymentProfile ?? "",
    };
}

function applyShowPopupToFormFields(p) {
    const paymentProfile = p.paymentProfile || "";
    const hasProfile = !!paymentProfile;
    return {
        layout: getCardConnectPayLayout(paymentProfile),
        formFields: {
            CARDCONNECT_PAYMENT_METHOD: hasProfile ? "profile" : "card",
            CARDCONNECT_TRANS_NO: p.transactionNumber ?? "",
            CARDCONNECT_CUSTOMER: p.customer ?? "",
            CARDCONNECT_AMOUNT: p.amount != null && p.amount !== "" ? Number(p.amount) : null,
            CARDCONNECT_PROFILE: paymentProfile,
            CARDCONNECT_ACCOUNT: "",
        },
    };
}

function CardConnectPayPopup(props) {
    const { showPopup = {}, setShowPopup, onConfirm } = props;
    const visible = showPopup.visible === true;
    const currentUser = props.oidc?.user;
    const formRefs = useRef({});

    const [popupFormFields, setPopupFormFields] = useState(initialPopupFormFields);
    const [defaultLayout, setDefaultLayout] = useState(() => getCardConnectPayLayout(""));

    /** Form.js runs prepareCardConnectPayOpen (verify + FNOM amount) + load panel before opening. */
    useEffect(() => {
        if (!visible || !showPopup.headerRowId) {
            return;
        }
        const payload = cardConnectPayloadFromShowPopup(showPopup);
        const { layout, formFields } = applyShowPopupToFormFields(payload);
        setDefaultLayout(layout);
        setPopupFormFields(formFields);
    }, [
        visible,
        showPopup.headerRowId,
        showPopup.transactionNumber,
        showPopup.customer,
        showPopup.amount,
        showPopup.paymentProfile,
    ]);

    const onFormLoad = useCallback(() => {}, []);

    useEffect(() => {
        if (!visible) return;
        const method = popupFormFields.CARDCONNECT_PAYMENT_METHOD;
        const accountReadOnly = method !== "profile";
        const id = setTimeout(() => {
            const inst = formRefs.current?.CARDCONNECT_ACCOUNT?.instance?.();
            if (inst) {
                try {
                    inst.option("readOnly", accountReadOnly);
                    if (accountReadOnly) inst.option("value", "");
                } catch (_) {}
            }
        }, 0);
        return () => clearTimeout(id);
    }, [visible, popupFormFields.CARDCONNECT_PAYMENT_METHOD]);

    const handleHiding = useCallback(() => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    const handleFieldValueChange = useCallback((name, value) => {
        if (name === "CARDCONNECT_PAYMENT_METHOD" && value !== "profile") {
            setPopupFormFields((prev) => ({ ...prev, [name]: value, CARDCONNECT_ACCOUNT: "" }));
        }
    }, []);

    const handleOk = useCallback(() => {
        if (!onConfirm || !showPopup.headerRowId) {
            handleHiding();
            return;
        }
        const method = popupFormFields.CARDCONNECT_PAYMENT_METHOD;
        let profileToUse = "";
        let newProfile = "";
        if (method === "profile") {
            const profile = (popupFormFields.CARDCONNECT_PROFILE || "").trim();
            const account = (popupFormFields.CARDCONNECT_ACCOUNT || "").trim();
            profileToUse = profile + (account ? "/" + account : "");
        } else if (method === "newProfile") {
            newProfile = "New";
        }
        onConfirm(showPopup.headerRowId, profileToUse, newProfile);
        handleHiding();
    }, [onConfirm, showPopup.headerRowId, popupFormFields, handleHiding]);

    if (!visible) return null;

    return (
        <Popup
            visible={visible}
            onHiding={handleHiding}
            dragEnabled={false}
            hideOnOutsideClick={true}
            showCloseButton={true}
            showTitle={true}
            title="Card Connect Payment"
            width={880}
            height="auto"
        >
            <div style={{ padding: "8px 0" }}>
                <FormField
                    currrentUser={currentUser}
                    layoutId={CARDCONNECT_LAYOUT_ID}
                    layoutName={CARDCONNECT_LAYOUT_NAME}
                    defaultFormSchema={defaultLayout}
                    formFields={popupFormFields}
                    setformFields={setPopupFormFields}
                    handleFieldValueChange={handleFieldValueChange}
                    onFormLoad={onFormLoad}
                    DYNAMICFORMSETTING="No"
                    formRefs={formRefs}
                    hasOnlyOneColumn={true}
                />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                    <Button text="Cancel" icon="close" onClick={handleHiding} />
                    <Button text="OK" type="default" icon="check" onClick={handleOk} />
                </div>
            </div>
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CardConnectPayPopup);
